import React, { useState, useEffect, useContext, useRef } from "react";
import Modal from "react-modal";
import {
  Checkbox,
  FormControlLabel,
  TextField,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import "./windowComps.scss";
import {
  adjustModalPositionAndSize,
  hasProperty,
  getRepresentatives,
  convertDataXYFromUnitsToUnits,
  convertRangeFromUnitsToUnits,
} from "../../../utils/helpers";
import { GeneralContext } from "../../../context/GeneralContext";
import { AuthContext } from "../../../context/AuthContext";
import CustomButton from "../../commonComponents/CustomButton";
import CloseButton from "../../commonComponents/CloseButton";
import HtmlTooltip from "../../commonComponents/HtmlTooltip";
import units from "../../../utils/units";

function ExportCurvesModal(props) {
  const {
    exportModalIsOpen,
    setExportModalIsOpen,
    reference,
    onExport,
    zIndex,
    graphsToInclude,
    listOfLoopCurvesToSave,
  } = props;

  const { recordedErrorLog } = useContext(GeneralContext);
  const { checkAccessLevel } = useContext(AuthContext);

  const [selectedCurves, setSelectedCurves] = useState(new Set());
  const [modalPlaceAndSize, setModalPlaceAndSize] = useState({
    top: "0",
    left: "0",
    height: "300px",
    width: "400px",
  });
  // State to track edited curve statistics: { curveKey: { min, max, nPoints } }
  const [curveStatsState, setCurveStatsState] = useState({});
  // State to track prefix/export name toggle for each section: { sectionKey: "prefix" | "export name" }
  const [sectionPrefixToggle, setSectionPrefixToggle] = useState({});
  // State to track export names for each curve: { curveKey: string }
  const [curveExportNames, setCurveExportNames] = useState({});
  // State to track log grid for each curve: { curveKey: boolean }
  const [curveLogGridState, setCurveLogGridState] = useState({});
  // State to track unit for each curve: { curveKey: unit object }
  const [curveUnitState, setCurveUnitState] = useState({});

  // Ref to track if we've initialized state for the current modal open session
  const hasInitializedRef = useRef(false);

  // Derive graphData and graphLayout from graphsToInclude
  const graphData = React.useMemo(() => {
    try {
      if (!graphsToInclude || graphsToInclude.length === 0) {
        return [];
      }
      // Aggregate plotData from all graphs, attaching each graph's layout to its plot entries
      const allPlotData = graphsToInclude.flatMap((graph) => {
        const plotData = graph.plotData || [];
        return plotData.map((plot) => ({
          ...plot,
          graphLayout: graph.layout,
          graphId: graph.id,
        }));
      });

      // If multiple graphs (for loop), exclude non-model curves (only include modelId curves)
      let filteredData = allPlotData;
      if (listOfLoopCurvesToSave) {
        filteredData = allPlotData.filter((plot) =>
          hasProperty(plot, "modelId")
        );
      }

      // Combine data curves with the same fileId, legendgroup, and graphId
      // (cut data curves appear as separate entries but should be combined)
      const combinedData = [];
      const processedKeys = new Set();

      filteredData.forEach((plot) => {
        // For data curves, create a key based on fileId, legendgroup, and graphId
        if (hasProperty(plot, "fileId")) {
          const legendGroup = plot.legendgroup || plot.name;
          const key = `file-${plot.fileId}-${legendGroup}-${plot.graphId}`;

          if (!processedKeys.has(key)) {
            processedKeys.add(key);

            // Find all curves with the same fileId, legendgroup, and graphId
            const matchingCurves = filteredData.filter((p) => {
              if (!hasProperty(p, "fileId")) return false;
              const pLegendGroup = p.legendgroup || p.name;
              return (
                p.fileId === plot.fileId &&
                pLegendGroup === legendGroup &&
                p.graphId === plot.graphId
              );
            });

            // Combine x and y arrays from all matching curves
            const combinedX = [];
            const combinedY = [];
            matchingCurves.forEach((curve) => {
              if (curve.x && curve.y && curve.x.length === curve.y.length) {
                combinedX.push(...curve.x);
                combinedY.push(...curve.y);
              }
            });

            // Create a combined curve entry
            if (combinedX.length > 0) {
              combinedData.push({
                ...plot,
                x: combinedX,
                y: combinedY,
              });
            }
          }
        } else {
          // For non-file curves (model, variable), add as-is
          combinedData.push(plot);
        }
      });

      return combinedData;
    } catch (error) {
      recordedErrorLog(
        "Error trying to create graphData: ",
        error,
        true,
        "Interface Error.",
        { graphsToInclude: graphsToInclude }
      );
      return [];
    }
  }, [graphsToInclude]);

  // const graphLayout = React.useMemo(() => {
  //   if (!graphsToInclude || graphsToInclude.length === 0) {
  //     return null;
  //   }
  //   // Use the first graph's layout as default (for now, single graph mode)
  //   return graphsToInclude[0]?.layout || null;
  // }, [graphsToInclude]);

  // Derive distList from graphData
  const distList = React.useMemo(() => {
    try {
      if (!graphData || graphData.length === 0) {
        return [];
      }
      return getRepresentatives(graphData);
    } catch (error) {
      recordedErrorLog(
        "Error trying to create distList: ",
        error,
        true,
        "Interface Error.",
        { graphData: graphData }
      );
      return [];
    }
  }, [graphData]);

  const getCurveKey = (dist) => {
    try {
      if (listOfLoopCurvesToSave) {
        if (hasProperty(dist, "modelId")) {
          return `model-${dist.modelId}-${dist.quantity}-${dist.graphId}`;
        } else if (hasProperty(dist, "fileId")) {
          return `file-${dist.fileId}-${dist.graphId}`;
        } else if (hasProperty(dist, "variableId")) {
          return `variable-${dist.variableId}-${dist.graphId}`;
        }
      } else {
        if (hasProperty(dist, "modelId")) {
          return `model-${dist.modelId}-${dist.quantity}`;
        } else if (hasProperty(dist, "fileId")) {
          return `file-${dist.fileId}`;
        } else if (hasProperty(dist, "variableId")) {
          return `variable-${dist.variableId}`;
        }
      }
      return null;
    } catch (error) {
      recordedErrorLog(
        "Error trying to create getCurveKey: ",
        error,
        true,
        "Interface Error.",
        { dist: dist }
      );
      return null;
    }
  };

  // Get curve statistics from graphData
  const getCurveStats = (dist) => {
    try {
      if (!graphData) return null;

      // Find matching curve in graphData
      const matchingCurve = graphData.find((curve) => {
        if (hasProperty(dist, "modelId")) {
          const modelMatch =
            hasProperty(curve, "modelId") &&
            curve.modelId === dist.modelId &&
            curve.quantity === dist.quantity;
          // Only check graphId if we have multiple graphs or if graphId is present
          if (listOfLoopCurvesToSave) {
            return modelMatch && curve.graphId === dist.graphId;
          }
          return modelMatch;
        } else if (hasProperty(dist, "fileId")) {
          const fileMatch =
            hasProperty(curve, "fileId") && curve.fileId === dist.fileId;
          // Only check graphId if we have multiple graphs or if graphId is present
          if (listOfLoopCurvesToSave) {
            return fileMatch && curve.graphId === dist.graphId;
          }
          return fileMatch;
        } else if (hasProperty(dist, "variableId")) {
          const variableMatch =
            hasProperty(curve, "variableId") &&
            curve.variableId === dist.variableId;
          // Only check graphId if we have multiple graphs or if graphId is present
          if (listOfLoopCurvesToSave) {
            return variableMatch && curve.graphId === dist.graphId;
          }
          return variableMatch;
        }
        return false;
      });

      if (!matchingCurve || !matchingCurve.x || matchingCurve.x.length === 0) {
        return null;
      }
      const graphUnit = matchingCurve.graphLayout?.xaxis?.unit || units[0];
      const convertedData = convertDataXYFromUnitsToUnits(
        units[0],
        graphUnit,
        matchingCurve.x,
        matchingCurve.y,
        true
      );

      const xValues = convertedData[0];
      const nPoints = xValues.length;
      let minX = Math.min(...xValues);
      let maxX = Math.max(...xValues);

      // Convert min and max to the graph's display unit if not cm^-1
      return {
        min: minX,
        max: maxX,
        nPoints: nPoints,
        graphUnit: graphUnit,
        isLog: matchingCurve.graphLayout?.xaxis?.type === "log" || false,
      };
    } catch (error) {
      recordedErrorLog(
        "Error trying to create getCurveStats: ",
        error,
        true,
        "Interface Error.",
        { dist: dist, graphData: graphData }
      );
      return null;
    }
  };

  // Categorize curves into three sections
  const categorizeCurves = () => {
    try {
      const parameterTracking = [];
      const modelCurves = [];
      const dataCurves = [];

      distList.forEach((dist) => {
        if (hasProperty(dist, "variableId")) {
          parameterTracking.push(dist);
        } else if (hasProperty(dist, "modelId")) {
          modelCurves.push(dist);
        } else if (hasProperty(dist, "fileId")) {
          dataCurves.push(dist);
        }
      });

      // Sort model curves by modelId to group curves from the same model together
      modelCurves.sort((a, b) => {
        // First sort by modelId
        if (a.modelId !== b.modelId) {
          return a.modelId - b.modelId;
        }
        // If same modelId, sort by quantity to keep consistent ordering
        if (a.quantity !== b.quantity) {
          return a.quantity - b.quantity;
        }
        // If same modelId and quantity, sort by graphId to keep consistent ordering
        return a.graphId - b.graphId;
      });

      return { parameterTracking, modelCurves, dataCurves };
    } catch (error) {
      recordedErrorLog(
        "Error trying to create categorizeCurves: ",
        error,
        true,
        "Interface Error.",
        { distList: distList }
      );
      return { parameterTracking: [], modelCurves: [], dataCurves: [] };
    }
  };

  useEffect(() => {
    try {
      if (exportModalIsOpen && distList && distList.length > 0) {
        // Only initialize state when modal first opens (not already initialized)
        if (!hasInitializedRef.current) {
          // Initialize with parameter tracking and model curves selected by default
          // Data curves are NOT selected by default
          const selectedKeys = new Set();
          const initialStats = {};
          const initialLogGrid = {};
          const initialUnits = {};
          const initialExportNames = {};

          // If listOfLoopCurvesToSave is provided, use it to pre-populate state
          if (listOfLoopCurvesToSave) {
            distList.forEach((dist) => {
              const curveKey = getCurveKey(dist);
              if (!curveKey) return;

              // Find matching entry in listOfLoopCurvesToSave
              const matchingEntry = listOfLoopCurvesToSave.find((entry) => {
                if (hasProperty(dist, "modelId")) {
                  const modelMatch =
                    entry.modelId === dist.modelId &&
                    entry.quantity === dist.quantity;
                  // Check graphId if we have multiple graphs
                  if (listOfLoopCurvesToSave) {
                    return modelMatch && entry.graphId === dist.graphId;
                  }
                  return modelMatch;
                }
                return false;
              });

              if (matchingEntry) {
                // Pre-populate with values from listOfLoopCurvesToSave
                selectedKeys.add(curveKey);
                if (hasProperty(dist, "modelId")) {
                  initialStats[curveKey] = {
                    min: matchingEntry.min,
                    max: matchingEntry.max,
                    nPoints: matchingEntry.nPoints,
                  };
                  initialLogGrid[curveKey] = matchingEntry.log || false;
                }
                initialUnits[curveKey] = matchingEntry.unit || units[0];
                // Extract export name from the name if it's different from default
                // (This is a simple approach - you might want to refine this)
                initialExportNames[curveKey] = matchingEntry.name || "";
                setSectionPrefixToggle((prev) => ({
                  ...prev,
                  "model-curves": "export name",
                }));
              } else {
                // No matching entry - don't select by default, but still initialize stats for display
                // Initialize stats state for model curves (for display purposes)
                const stats = getCurveStats(dist);
                if (hasProperty(dist, "modelId")) {
                  if (stats) {
                    initialStats[curveKey] = {
                      min: stats.min,
                      max: stats.max,
                      nPoints: stats.nPoints,
                    };
                    initialLogGrid[curveKey] = stats.isLog;
                  }
                }
                if (stats) {
                  initialUnits[curveKey] = stats.graphUnit;
                }
                // Note: curve is NOT added to selectedKeys, so it won't be selected by default
              }
            });
          } else {
            // No listOfLoopCurvesToSave provided, use default initialization
            distList.forEach((dist) => {
              const curveKey = getCurveKey(dist);
              if (curveKey) {
                // Only select if it's NOT a data curve (fileId)
                if (
                  hasProperty(dist, "variableId") ||
                  hasProperty(dist, "modelId")
                ) {
                  selectedKeys.add(curveKey);
                }

                // Initialize stats state for model curves
                const stats = getCurveStats(dist);
                if (hasProperty(dist, "modelId")) {
                  if (stats) {
                    initialStats[curveKey] = {
                      min: stats.min,
                      max: stats.max,
                      nPoints: stats.nPoints,
                    };
                    initialLogGrid[curveKey] = stats.isLog;
                  }
                }
                if (stats) {
                  initialUnits[curveKey] = stats.graphUnit;
                }
              }
            });
          }

          setSelectedCurves(selectedKeys);
          setCurveStatsState(initialStats);
          setCurveLogGridState(initialLogGrid);
          setCurveUnitState(initialUnits);
          setCurveExportNames(initialExportNames);
          hasInitializedRef.current = true;
        }
      } else if (!exportModalIsOpen) {
        // Reset state when modal closes
        setCurveStatsState({});
        setCurveLogGridState({});
        setCurveUnitState({});
        setSectionPrefixToggle({});
        setCurveExportNames({});
        hasInitializedRef.current = false;
      }
    } catch (error) {
      recordedErrorLog(
        "Error trying to initialize categorizeCurves: ",
        error,
        true,
        "Interface Error.",
        { exportModalIsOpen: exportModalIsOpen, distList: distList }
      );
    }
  }, [exportModalIsOpen, distList, listOfLoopCurvesToSave, graphsToInclude]);

  useEffect(() => {
    if (exportModalIsOpen) {
      const modalSize = { width: 970, height: 600 };

      // Center the modal on the screen
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const centerX = (viewportWidth - modalSize.width) / 2;
      const centerY = (viewportHeight - modalSize.height) / 2;

      const modalPosition = {
        top: centerY,
        left: centerX,
        right: "auto",
      };

      const adjusted = adjustModalPositionAndSize(modalPosition, modalSize);

      setModalPlaceAndSize(adjusted);
    }
  }, [exportModalIsOpen, reference]);

  const handleCloseModal = () => {
    try {
      try {
        setExportModalIsOpen(false);
        setSelectedCurves(new Set());
        // Note: Other state resets are handled in the useEffect when exportModalIsOpen becomes false
      } catch (error) {
        recordedErrorLog("Export modal closing handler failure: ", error);
      }
    } catch (error) {
      recordedErrorLog(
        "Error trying to close export modal: ",
        error,
        true,
        "Interface Error.",
        { exportModalIsOpen: exportModalIsOpen }
      );
    }
  };

  const toggleCurveSelection = (curveKey) => {
    try {
      setSelectedCurves((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(curveKey)) {
          newSet.delete(curveKey);
        } else {
          newSet.add(curveKey);
        }
        return newSet;
      });
    } catch (error) {
      recordedErrorLog(
        "Error trying to toggle curve selection: ",
        error,
        true,
        "Interface Error.",
        { curveKey: curveKey, selectedCurves: selectedCurves }
      );
    }
  };

  const handleSelectAllInSection = (sectionCurves) => {
    try {
      const sectionKeys = new Set(
        sectionCurves.map((dist) => getCurveKey(dist)).filter(Boolean)
      );
      const allSelected =
        sectionKeys.size > 0 &&
        Array.from(sectionKeys).every((key) => selectedCurves.has(key));

      setSelectedCurves((prev) => {
        const newSet = new Set(prev);
        if (allSelected) {
          // Deselect all in this section
          sectionKeys.forEach((key) => newSet.delete(key));
        } else {
          // Select all in this section
          sectionKeys.forEach((key) => newSet.add(key));
        }
        return newSet;
      });
    } catch (error) {
      recordedErrorLog(
        "Error trying to select all in section: ",
        error,
        true,
        "Interface Error.",
        { sectionCurves: sectionCurves, selectedCurves: selectedCurves }
      );
    }
  };

  // Check if a curve's export name has duplicates
  const hasDuplicateExportName = (curveKey, sectionKey) => {
    // Only check if toggle is set to "export name" for this section
    try {
      const mode = sectionPrefixToggle[sectionKey] || "prefix";
      if (mode !== "export name") {
        return false;
      }

      // Only check if curve is selected
      if (!selectedCurves.has(curveKey)) {
        return false;
      }

      const exportName = curveExportNames[curveKey] || "";
      if (exportName.trim() === "") {
        return false;
      }

      // Count how many selected curves have this export name
      const allCurves = categorizeCurves();
      const allCurvesList = [
        ...allCurves.parameterTracking,
        ...allCurves.modelCurves,
        ...allCurves.dataCurves,
      ];

      let count = 0;
      for (const dist of allCurvesList) {
        const key = getCurveKey(dist);
        if (!key || !selectedCurves.has(key)) {
          continue;
        }

        // Determine which section this curve belongs to
        let distSectionKey = "";
        if (hasProperty(dist, "variableId")) {
          distSectionKey = "parameter-tracking";
        } else if (hasProperty(dist, "modelId")) {
          distSectionKey = "model-curves";
        } else if (hasProperty(dist, "fileId")) {
          distSectionKey = "data-curves";
        }

        // Only count curves in sections where toggle is "export name"
        const distMode = sectionPrefixToggle[distSectionKey] || "prefix";
        if (distMode === "export name") {
          const distExportName = curveExportNames[key] || "";
          if (distExportName.trim() === exportName.trim()) {
            count++;
          }
        }
      }

      return count >= 2;
    } catch (error) {
      recordedErrorLog(
        "Error trying to check for duplicate export name: ",
        error,
        true,
        "Interface Error.",
        {
          curveKey: curveKey,
          sectionKey: sectionKey,
          selectedCurves: selectedCurves,
        }
      );
      return false;
    }
  };

  // Check if a curve has log enabled and min <= 0
  const hasLogMinError = (curveKey) => {
    // Find the curve to get its stats
    try {
      const allCurves = categorizeCurves();
      const allCurvesList = [
        ...allCurves.parameterTracking,
        ...allCurves.modelCurves,
        ...allCurves.dataCurves,
      ];

      const dist = allCurvesList.find((d) => getCurveKey(d) === curveKey);
      if (!dist) {
        return false;
      }

      const defaultStats = getCurveStats(dist);
      const currentStats = curveStatsState[curveKey] || defaultStats;
      const isLog = curveLogGridState[curveKey] || false;

      if (!currentStats) {
        return false;
      }
      if (isLog === undefined || isLog === null || isLog === false) {
        return false;
      }

      const minValue =
        typeof currentStats.min === "string"
          ? parseFloat(currentStats.min)
          : currentStats.min;
      // Check if min is valid and <= 0
      if (!isNaN(minValue) && currentStats.min !== "" && minValue <= 0) {
        return true;
      }

      return false;
    } catch (error) {
      recordedErrorLog(
        "Error trying to check for log min error: ",
        error,
        true,
        "Interface Error.",
        { curveKey: curveKey }
      );
      return false;
    }
  };

  // Validate if export is allowed
  const isExportDisabled = () => {
    try {
      // Check 1: No curves selected (skip this check in loop mode)
      const isLoopMode = listOfLoopCurvesToSave !== undefined;
      if (!isLoopMode && selectedCurves.size === 0) {
        return true;
      }

      // Check 2: Any selected model curve has min >= max
      const allCurves = categorizeCurves();
      const allCurvesList = [
        ...allCurves.parameterTracking,
        ...allCurves.modelCurves,
        ...allCurves.dataCurves,
      ];

      for (const dist of allCurvesList) {
        const curveKey = getCurveKey(dist);
        if (!curveKey || !selectedCurves.has(curveKey)) {
          continue;
        }
        // Check if it's a model curve with invalid min/max
        if (hasProperty(dist, "modelId")) {
          const defaultStats = getCurveStats(dist);
          const currentStats = curveStatsState[curveKey] || defaultStats;

          if (currentStats) {
            const minValue =
              typeof currentStats.min === "string"
                ? parseFloat(currentStats.min)
                : currentStats.min;
            const maxValue =
              typeof currentStats.max === "string"
                ? parseFloat(currentStats.max)
                : currentStats.max;

            // Check if min >= max and both are valid numbers
            if (
              !isNaN(minValue) &&
              !isNaN(maxValue) &&
              currentStats.min !== "" &&
              currentStats.max !== "" &&
              minValue >= maxValue
            ) {
              return true;
            }

            // Check if log is enabled and min <= 0
            if (hasLogMinError(curveKey)) {
              return true;
            }
          }
        }
      }

      // Check 3: Duplicate export names when toggle is set to "export name"
      const exportNameCounts = {};

      for (const dist of allCurvesList) {
        const curveKey = getCurveKey(dist);
        if (!curveKey || !selectedCurves.has(curveKey)) {
          continue;
        }

        // Determine which section this curve belongs to
        let sectionKey = "";
        if (hasProperty(dist, "variableId")) {
          sectionKey = "parameter-tracking";
        } else if (hasProperty(dist, "modelId")) {
          sectionKey = "model-curves";
        } else if (hasProperty(dist, "fileId")) {
          sectionKey = "data-curves";
        }

        // Check if the toggle for this section is set to "export name"
        const mode = sectionPrefixToggle[sectionKey] || "prefix";
        if (mode === "export name") {
          const exportName = curveExportNames[curveKey] || "";
          // Only check non-empty export names
          if (exportName.trim() !== "") {
            exportNameCounts[exportName] =
              (exportNameCounts[exportName] || 0) + 1;
          }
        }
      }

      // Check if any export name appears 2+ times
      for (const count of Object.values(exportNameCounts)) {
        if (count >= 2) {
          return true;
        }
      }

      return false;
    } catch (error) {
      recordedErrorLog(
        "Error trying to check if export is disabled: ",
        error,
        true,
        "Interface Error.",
        {}
      );
      return false;
    }
  };

  const handleResetToOriginalSettings = () => {
    try {
      // Reset all states to original graph values
      const resetStats = {};
      const resetLogGrid = {};
      const resetUnits = {};
      const resetSelectedKeys = new Set();

      // Determine if we're in loop mode (listOfLoopCurvesToSave provided)
      const isLoopMode =
        listOfLoopCurvesToSave;

      // Get default values from graph for each curve
      distList.forEach((dist) => {
        const curveKey = getCurveKey(dist);
        if (curveKey) {
          // Reset selection based on mode
          if (isLoopMode) {
            // Loop mode: everything unchecked
            // Don't add to resetSelectedKeys
          } else {
            // Graph mode: model and variable selected, data unselected
            if (
              hasProperty(dist, "variableId") ||
              hasProperty(dist, "modelId")
            ) {
              resetSelectedKeys.add(curveKey);
            }
          }

          // Get stats from graph
          const stats = getCurveStats(dist);
          if (hasProperty(dist, "modelId")) {
            if (stats) {
              resetStats[curveKey] = {
                min: stats.min,
                max: stats.max,
                nPoints: stats.nPoints,
              };
              resetLogGrid[curveKey] = stats.isLog;
            }
          }
          if (stats) {
            resetUnits[curveKey] = stats.graphUnit;
          }
        }
      });

      // Update all states
      setSelectedCurves(resetSelectedKeys);
      setCurveStatsState(resetStats);
      setCurveLogGridState(resetLogGrid);
      setCurveUnitState(resetUnits);
      // Clear all export names/prefixes
      setCurveExportNames({});
    } catch (error) {
      recordedErrorLog(
        "Error trying to reset to original settings: ",
        error,
        true,
        "Interface Error.",
        {}
      );
    }
  };

  const handleExportClick = () => {
    try {
      if (isExportDisabled() || !checkAccessLevel(1)) {
        return;
      }
      onExport(
        selectedCurves,
        curveExportNames,
        sectionPrefixToggle,
        curveStatsState,
        curveLogGridState,
        curveUnitState
      );
      handleCloseModal();
    } catch (error) {
      recordedErrorLog(
        "Error trying to handle export click: ",
        error,
        true,
        "Interface Error.",
        {
          selectedCurves: selectedCurves,
          curveExportNames: curveExportNames,
          sectionPrefixToggle: sectionPrefixToggle,
          curveStatsState: curveStatsState,
          curveLogGridState: curveLogGridState,
          curveUnitState: curveUnitState,
        }
      );
    }
  };

  // Component to wrap text with tooltip if truncated
  const TruncatedTextWithTooltip = ({
    children,
    text,
    className,
    dangerouslySetInnerHTML,
  }) => {
    const textRef = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);

    useEffect(() => {
      const checkTruncation = () => {
        if (textRef.current) {
          const element = textRef.current;
          setIsTruncated(element.scrollWidth > element.clientWidth);
        }
      };

      checkTruncation();
      // Re-check on window resize
      window.addEventListener("resize", checkTruncation);
      // Re-check after a short delay to ensure rendering is complete
      const timeoutId = setTimeout(checkTruncation, 100);

      return () => {
        window.removeEventListener("resize", checkTruncation);
        clearTimeout(timeoutId);
      };
    }, [text, children]);

    const content = dangerouslySetInnerHTML ? (
      <div
        ref={textRef}
        className={className}
        dangerouslySetInnerHTML={dangerouslySetInnerHTML}
      />
    ) : (
      <div ref={textRef} className={className}>
        {children}
      </div>
    );

    if (isTruncated && text) {
      return (
        <HtmlTooltip
          title={
            <React.Fragment>
              <div className="customTooltip">{text}</div>
            </React.Fragment>
          }
        >
          {content}
        </HtmlTooltip>
      );
    }

    return content;
  };

  const renderCurveList = (curves, isModelSection = false, sectionKey = "") => {
    return curves.map((dist) => {
      const curveKey = getCurveKey(dist);
      if (!curveKey) return null;

      const isSelected = selectedCurves.has(curveKey);
      const isModel = hasProperty(dist, "modelId");
      const isDataCurve = hasProperty(dist, "fileId");

      const defaultStats = isModel
        ? getCurveStats(dist)
        : isDataCurve
        ? getCurveStats(dist)
        : null;
      const currentStats = curveStatsState[curveKey] || defaultStats;

      const handleStatChange = (field, value) => {
        try {
          if (!currentStats) return;

          setCurveStatsState((prev) => {
            const updated = { ...prev };
            if (!updated[curveKey]) {
              updated[curveKey] = { ...defaultStats };
            }
            updated[curveKey][field] = value;
            return updated;
          });
        } catch (error) {
          recordedErrorLog(
            "Error trying to handle stat change: ",
            error,
            true,
            "Interface Error.",
            {}
          );
        }
      };

      const handleUnitChangeData = (selectedUnit) => {
        try {
          if (!selectedUnit || !currentStats) return;

          // Update unit state
          setCurveUnitState((prev) => ({
            ...prev,
            [curveKey]: selectedUnit,
          }));

          // Find the matching curve in graphData to get the actual data points
          const matchingCurve = graphData.find((curve) => {
            if (hasProperty(dist, "fileId")) {
              const fileMatch =
                hasProperty(curve, "fileId") && curve.fileId === dist.fileId;
              return fileMatch;
            }
            return false;
          });

          if (matchingCurve && matchingCurve.x && matchingCurve.x.length > 0) {
            // Convert data points from internal format (cm^-1) to the new unit
            const convertedData = convertDataXYFromUnitsToUnits(
              units[0], // Internal format is always cm^-1
              selectedUnit,
              matchingCurve.x,
              matchingCurve.y,
              true // Sort the data
            );
            const xValues = convertedData[0];
            const newMin = Math.min(...xValues);
            const newMax = Math.max(...xValues);

            setCurveStatsState((prev) => {
              const updated = { ...prev };
              if (!updated[curveKey]) {
                updated[curveKey] = { ...currentStats };
              }
              updated[curveKey].min = newMin;
              updated[curveKey].max = newMax;
              updated[curveKey].nPoints = xValues.length;
              return updated;
            });
          }
        } catch (error) {
          recordedErrorLog(
            "Error trying to handle unit change data: ",
            error,
            true,
            "Interface Error.",
            { selectedUnit: selectedUnit }
          );
        }
      };

      const handleUnitChangeModel = (selectedUnit) => {
        try {
          if (!selectedUnit || !currentStats) return;

          // Update unit state
          setCurveUnitState((prev) => ({
            ...prev,
            [curveKey]: selectedUnit,
          }));

          // Get current unit
          const currentUnit = curveUnitState[curveKey] || units[0];

          // Parse min/max values (handle string inputs)
          const minValue =
            typeof currentStats.min === "string"
              ? parseFloat(currentStats.min)
              : currentStats.min;
          const maxValue =
            typeof currentStats.max === "string"
              ? parseFloat(currentStats.max)
              : currentStats.max;

          // Skip conversion if values are invalid
          if (!isNaN(minValue) && !isNaN(maxValue)) {
            // Convert current min/max from current unit to new unit
            let convertedRange = convertRangeFromUnitsToUnits(
              currentUnit,
              selectedUnit,
              [minValue, maxValue]
            );

            // Update stats state with converted values
            setCurveStatsState((prev) => {
              const updated = { ...prev };
              if (!updated[curveKey]) {
                updated[curveKey] = {
                  ...defaultStats,
                };
              }
              updated[curveKey].min = convertedRange[0];
              updated[curveKey].max = convertedRange[1];
              return updated;
            });
          }
        } catch (error) {
          recordedErrorLog(
            "Error trying to handle unit change model: ",
            error,
            true,
            "Interface Error.",
            { selectedUnit: selectedUnit }
          );
        }
      };

      const validateFloat = (value) => {
        if (value === "" || value === "-" || value === "+") return true; // Allow intermediate states
        // Allow floats and scientific notation: -1.23e-4, 1.5e+10, etc.
        // Allow intermediate states like "1e", "1e-", "1e+", "1.23e", etc.
        const floatRegex = /^-?\d*\.?\d*([eE][+-]?\d*)?$/;
        return floatRegex.test(value);
      };

      const validateInteger = (value) => {
        if (value === "") return true; // Allow empty during typing
        // Only positive integers
        const intRegex = /^\d+$/;
        return intRegex.test(value) && parseInt(value, 10) > 0;
      };

      const validateFilename = (value) => {
        // Allow only letters (a-z, A-Z), numbers (0-9), and underscore (_)
        // Disallow /, ., , and other special characters
        const filenameRegex = /^[a-zA-Z0-9_]*$/;
        return filenameRegex.test(value);
      };

      const mode = sectionPrefixToggle[sectionKey] || "prefix";
      const isPrefixMode = mode === "prefix";
      const exportName = curveExportNames[curveKey] || "";

      const handleExportNameChange = (value) => {
        // Only update if the value is valid (allows empty string)
        if (validateFilename(value)) {
          setCurveExportNames((prev) => {
            const updated = { ...prev };
            updated[curveKey] = value;
            return updated;
          });
        }
      };

      return (
        <div key={curveKey} className="exportCurveEntry">
          <FormControlLabel
            control={
              <Checkbox
                checked={isSelected}
                onChange={() => toggleCurveSelection(curveKey)}
              />
            }
            label={
              <div
                className={`exportCurveName ${
                  isModelSection || isDataCurve
                    ? "modelCurveRow"
                    : "modelCurveRow"
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
              >
                {isModel || isDataCurve ? (
                  <>
                    <div className="modelNameArea">
                      <TruncatedTextWithTooltip
                        text={dist.name}
                        className="modelNameInline"
                        dangerouslySetInnerHTML={{
                          __html: (() => {
                            if (isModel) {
                              const lastColonIndex = dist.name.lastIndexOf(":");
                              if (lastColonIndex !== -1) {
                                const modelName = dist.name.substring(
                                  0,
                                  lastColonIndex + 1
                                );
                                const quantityName = dist.name
                                  .substring(lastColonIndex + 1)
                                  .trim();
                                return (
                                  modelName +
                                  " <span style='font-style: italic;'>" +
                                  quantityName +
                                  "</span>"
                                );
                              }
                            }
                            return dist.name;
                          })(),
                        }}
                      />
                    </div>
                    <TextField
                      size="small"
                      value={exportName}
                      onChange={(e) => handleExportNameChange(e.target.value)}
                      placeholder={isPrefixMode ? "Prefix name" : "Export name"}
                      className="exportNameInput"
                      error={hasDuplicateExportName(curveKey, sectionKey)}
                    />
                    {currentStats &&
                      (isModelSection || sectionKey === "data-curves") &&
                      (() => {
                        // Check if min >= max for validation
                        const minValue =
                          typeof currentStats.min === "string"
                            ? parseFloat(currentStats.min)
                            : currentStats.min;
                        const maxValue =
                          typeof currentStats.max === "string"
                            ? parseFloat(currentStats.max)
                            : currentStats.max;
                        const hasMinMaxError =
                          !isNaN(minValue) &&
                          !isNaN(maxValue) &&
                          minValue >= maxValue &&
                          currentStats.min !== "" &&
                          currentStats.max !== "";

                        // Check if log is enabled and min <= 0
                        const hasLogError = hasLogMinError(curveKey);
                        const hasError = hasMinMaxError || hasLogError;

                        return (
                          <div className="modelCurveStatsColumns">
                            {/* Log grid checkbox - only for model curves */}
                            {sectionKey === "model-curves" ? (
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={
                                      curveLogGridState[curveKey] || false
                                    }
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      setCurveLogGridState((prev) => ({
                                        ...prev,
                                        [curveKey]: e.target.checked,
                                      }));
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                    }}
                                    size="small"
                                  />
                                }
                                className="logGridCheckbox"
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                              />
                            ) : (
                              <div className="logGridCheckbox logGridEmpty"></div>
                            )}
                            {/* Min/Max/NPoints - input fields for models, text for data curves */}
                            {sectionKey === "model-curves" ? (
                              <>
                                <TextField
                                  size="small"
                                  value={currentStats.min ?? ""}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (validateFloat(value)) {
                                      handleStatChange("min", value);
                                    }
                                  }}
                                  onBlur={(e) => {
                                    const value = parseFloat(e.target.value);
                                    if (!isNaN(value)) {
                                      handleStatChange("min", value);
                                    } else if (defaultStats) {
                                      handleStatChange("min", defaultStats.min);
                                    }
                                  }}
                                  error={hasLogError || hasMinMaxError}
                                  className="modelCurveStatInput"
                                />
                                <TextField
                                  size="small"
                                  value={currentStats.max ?? ""}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (validateFloat(value)) {
                                      handleStatChange("max", value);
                                    }
                                  }}
                                  onBlur={(e) => {
                                    const value = parseFloat(e.target.value);
                                    if (!isNaN(value)) {
                                      handleStatChange("max", value);
                                    } else if (defaultStats) {
                                      handleStatChange("max", defaultStats.max);
                                    }
                                  }}
                                  error={hasError}
                                  className="modelCurveStatInput"
                                />
                                <TextField
                                  size="small"
                                  value={currentStats.nPoints ?? ""}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (validateInteger(value)) {
                                      handleStatChange(
                                        "nPoints",
                                        value === "" ? "" : parseInt(value, 10)
                                      );
                                    }
                                  }}
                                  onBlur={(e) => {
                                    const value = parseInt(e.target.value, 10);
                                    if (!isNaN(value) && value > 0) {
                                      handleStatChange("nPoints", value);
                                    } else if (defaultStats) {
                                      handleStatChange(
                                        "nPoints",
                                        defaultStats.nPoints
                                      );
                                    }
                                  }}
                                  className="modelCurveStatInput"
                                />
                              </>
                            ) : (
                              <>
                                <div className="modelCurveStatValue">
                                  {currentStats.min?.toFixed(2) ?? ""}
                                </div>
                                <div className="modelCurveStatValue">
                                  {currentStats.max?.toFixed(2) ?? ""}
                                </div>
                                <div className="modelCurveStatValue">
                                  {currentStats.nPoints ?? ""}
                                </div>
                              </>
                            )}
                            <FormControl
                              size="small"
                              className="unitSelectControl"
                            >
                              <Select
                                value={
                                  curveUnitState[curveKey]?.value ||
                                  units[0].value
                                }
                                onChange={(e) => {
                                  const selectedUnit = units.find(
                                    (u) => u.value === e.target.value
                                  );
                                  if (!selectedUnit) return;

                                  if (isDataCurve) {
                                    handleUnitChangeData(selectedUnit);
                                  } else if (isModel) {
                                    handleUnitChangeModel(selectedUnit);
                                  } else {
                                    // For other curve types, just update the unit
                                    setCurveUnitState((prev) => ({
                                      ...prev,
                                      [curveKey]: selectedUnit,
                                    }));
                                  }
                                }}
                                className="unitSelect"
                              >
                                {units.map((unit) => (
                                  <MenuItem key={unit.value} value={unit.value}>
                                    {unit.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </div>
                        );
                      })()}
                  </>
                ) : (
                  <>
                    <TruncatedTextWithTooltip
                      text={dist.name}
                      className="exportCurveNameText"
                      dangerouslySetInnerHTML={{ __html: dist.name }}
                    />
                    <TextField
                      size="small"
                      value={exportName}
                      onChange={(e) => handleExportNameChange(e.target.value)}
                      placeholder={isPrefixMode ? "Prefix name" : "Export name"}
                      className="exportNameInput"
                      error={hasDuplicateExportName(curveKey, sectionKey)}
                    />
                    {/* Parameter tracking section - no additional fields */}
                  </>
                )}
              </div>
            }
          />
        </div>
      );
    });
  };

  const renderSection = (title, curves, sectionKey) => {
    if (curves.length === 0) {
      return null;
    }

    const sectionKeys = new Set(
      curves.map((dist) => getCurveKey(dist)).filter(Boolean)
    );
    const allSelected =
      sectionKeys.size > 0 &&
      Array.from(sectionKeys).every((key) => selectedCurves.has(key));
    const someSelected =
      sectionKeys.size > 0 &&
      Array.from(sectionKeys).some((key) => selectedCurves.has(key));

    const isModelSection = sectionKey === "model-curves";
    const isDataSection = sectionKey === "data-curves";

    return (
      <div key={sectionKey} className="exportSection">
        <div className="exportSectionHeader">
          <FormControlLabel
            control={
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected && !allSelected}
                onChange={() => handleSelectAllInSection(curves)}
              />
            }
            label={
              <div
                className={`exportSectionHeaderContent ${
                  isModelSection || isDataSection ? "modelSectionHeader" : ""
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
              >
                <div className="exportSectionTitle">{title}</div>
                {isModelSection || isDataSection ? (
                  <>
                    <div className="exportSectionPrefixToggle">
                      <div className="prefixToggleButtons">
                        <div
                          className={`prefixToggleButton${
                            sectionPrefixToggle[sectionKey] === "prefix" ||
                            !sectionPrefixToggle[sectionKey]
                              ? " selected"
                              : ""
                          }`}
                          onClick={() => {
                            setSectionPrefixToggle((prev) => ({
                              ...prev,
                              [sectionKey]: "prefix",
                            }));
                          }}
                        >
                          prefix
                        </div>
                        <div
                          className={`prefixToggleButton${
                            sectionPrefixToggle[sectionKey] === "export name"
                              ? " selected"
                              : ""
                          }`}
                          onClick={() => {
                            setSectionPrefixToggle((prev) => ({
                              ...prev,
                              [sectionKey]: "export name",
                            }));
                          }}
                        >
                          export name
                        </div>
                      </div>
                    </div>
                    <div className="exportSectionColumnHeaders">
                      {isModelSection ? (
                        <div className="exportColumnHeaderLogGrid">Log</div>
                      ) : (
                        <div className="exportColumnHeaderLogGrid"></div>
                      )}
                      <div className="exportColumnHeader">Min</div>
                      <div className="exportColumnHeader">Max</div>
                      <div className="exportColumnHeader">Npoints</div>
                      <div className="exportColumnHeader">Unit</div>
                    </div>
                  </>
                ) : (
                  <div className="exportSectionPrefixToggle">
                    <div className="prefixToggleButtons">
                      <div
                        className={`prefixToggleButton${
                          sectionPrefixToggle[sectionKey] === "prefix" ||
                          !sectionPrefixToggle[sectionKey]
                            ? " selected"
                            : ""
                        }`}
                        onClick={() => {
                          setSectionPrefixToggle((prev) => ({
                            ...prev,
                            [sectionKey]: "prefix",
                          }));
                        }}
                      >
                        prefix
                      </div>
                      <div
                        className={`prefixToggleButton${
                          sectionPrefixToggle[sectionKey] === "export name"
                            ? " selected"
                            : ""
                        }`}
                        onClick={() => {
                          setSectionPrefixToggle((prev) => ({
                            ...prev,
                            [sectionKey]: "export name",
                          }));
                        }}
                      >
                        export name
                      </div>
                    </div>
                  </div>
                )}
              </div>
            }
          />
        </div>
        <div className="exportSectionList">
          {renderCurveList(curves, isModelSection || isDataSection, sectionKey)}
        </div>
      </div>
    );
  };

  const allCurves = categorizeCurves();

  return (
    <Modal
      isOpen={exportModalIsOpen}
      onRequestClose={handleCloseModal}
      shouldCloseOnOverlayClick={true}
      contentLabel="Export Curves Modal"
      appElement={reference?.current || undefined}
      style={{
        content: {
          width: modalPlaceAndSize.width,
          height: modalPlaceAndSize.height,
          top: modalPlaceAndSize.top,
          left: modalPlaceAndSize.left,
          right: modalPlaceAndSize.right,
          padding: 0,
          border: "1px solid black",
          borderRadius: "10px",
        },
        overlay: {
          zIndex: zIndex + 200,
        },
      }}
      id="export-curves-modal"
    >
      <div className="exportCurvesModal">
        <div className="exportModalHeaderButtons">
          <div className="exportModalTitle">Select Curves to Export</div>
          <CustomButton
            text="Original Settings"
            extraClassnames={["noMargin"]}
            handleClick={handleResetToOriginalSettings}
            disabled={false}
            id="reset-original-settings-button"
          />
          {!checkAccessLevel(1) ? (
            <HtmlTooltip
              title={
                <div className="customTooltip">
                  Please upgrade your subscription to use this feature.
                </div>
              }
            >
              <div>
                <CustomButton
                  text={
                    listOfLoopCurvesToSave 
                      ? "Save Selection"
                      : "Export"
                  }
                  extraClassnames={["noMargin"]}
                  handleClick={handleExportClick}
                  disabled={isExportDisabled() || !checkAccessLevel(1)}
                  id="confirm-export-button"
                />
              </div>
            </HtmlTooltip>
          ) : (
            <CustomButton
              text={
                listOfLoopCurvesToSave 
                  ? "Save Selection"
                  : "Export"
              }
              extraClassnames={["noMargin"]}
              handleClick={handleExportClick}
              disabled={isExportDisabled() || !checkAccessLevel(1)}
              id="confirm-export-button"
            />
          )}
          <div className="closeButtonTopRight" id="close-export-curves-button">
            <CloseButton onClose={handleCloseModal} />
          </div>
        </div>
        <div className="exportModalContent">
          <div className="exportCurvesSections">
            {renderSection(
              "Select parameter tracking curves",
              allCurves.parameterTracking,
              "parameter-tracking"
            )}
            {renderSection(
              "Select modelcurves",
              allCurves.modelCurves,
              "model-curves"
            )}
            {renderSection(
              "Select datacurves",
              allCurves.dataCurves,
              "data-curves"
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default ExportCurvesModal;
